import React from "react";
import { Container } from "react-bootstrap";
import {
  AiFillGithub,
  AiOutlineDownload,
  AiOutlineLink,
  AiOutlineMail,
  AiOutlinePhone,
} from "react-icons/ai";
import { FaLinkedinIn } from "react-icons/fa";
import Particle from "../../components/common/Particle";
import profilePhoto from "../../assets/DSCF5719.JPG";
import "./Resume.css";
import { resumeData } from "./resumeData";

const headerIconMap = {
  github: AiFillGithub,
  linkedin: FaLinkedinIn,
  website: AiOutlineLink,
  pdf: AiOutlineDownload,
};

function ResumeHeader({ header }) {
  return (
    <header className="resume-cv__title-block">
      <div className="resume-cv__title-wrapper">
        <hr className="resume-cv__title-rule resume-cv__title-rule--top" />
        <h1 className="resume-cv__title">{header.name}</h1>
        <hr className="resume-cv__title-rule resume-cv__title-rule--bottom" />
      </div>

      <img
        src={profilePhoto}
        alt={header.name}
        className="resume-cv__author-photo"
      />

      <div className="resume-cv__affiliation">
        {header.affiliation.map((line) => (
          <div key={line}>{line}</div>
        ))}
      </div>

      <div className="resume-cv__contact-info">
        {header.contact.map((item, index) => {
          const Icon = item.icon === "mail" ? AiOutlineMail : AiOutlinePhone;

          return (
            <React.Fragment key={item.href}>
              <span className="resume-cv__contact-item">
                <Icon className="resume-cv__contact-icon" />
                <a href={item.href}>{item.text}</a>
              </span>
              {index < header.contact.length - 1 && (
                <span className="resume-cv__separator">|</span>
              )}
            </React.Fragment>
          );
        })}
      </div>

      <div className="resume-cv__links">
        {header.links.map((link) => {
          const Icon = headerIconMap[link.icon];

          return (
            <a
              key={link.href}
              href={link.href}
              target="_blank"
              rel="noreferrer"
              className="resume-cv__icon-link"
              aria-label={link.label}
              title={link.label}
            >
              <Icon />
            </a>
          );
        })}
      </div>
    </header>
  );
}

function ResumeAbstract({ abstract }) {
  return (
    <section className="resume-cv__abstract">
      <h2 className="resume-cv__abstract-heading">Profile</h2>
      <div className="resume-cv__abstract-content">
        {abstract.paragraphs.map((paragraph, index) => (
          <p key={index} dangerouslySetInnerHTML={{ __html: paragraph }} />
        ))}
      </div>
      <div className="resume-cv__keywords">
        <span className="resume-cv__keywords-label">Focus:</span>{" "}
        {abstract.keywords.join(" | ")}
      </div>
    </section>
  );
}

function ResumeEntry({ entry }) {
  return (
    <div className="resume-cv__entry">
      <div className="resume-cv__entry-line">
        <span className="resume-cv__entry-title">{entry.title}</span>
        <span className="resume-cv__entry-date">{entry.date}</span>
      </div>

      {(entry.detail || entry.links?.length) && (
        <div className="resume-cv__entry-detail">
          {entry.detail && (
            <span dangerouslySetInnerHTML={{ __html: entry.detail }} />
          )}
          {entry.links?.length ? (
            <span className="resume-cv__publication-links">
              {entry.links.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  target="_blank"
                  rel="noreferrer"
                >
                  [{link.text}]
                </a>
              ))}
            </span>
          ) : null}
        </div>
      )}

      {entry.items?.length ? (
        <ul className="resume-cv__entry-list">
          {entry.items.map((item, index) => (
            <li key={index} dangerouslySetInnerHTML={{ __html: item }} />
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function ResumeSection({ index, section }) {
  return (
    <section className="resume-cv__section">
      <h2 className="resume-cv__section-heading">
        {index + 1}. {section.title}
      </h2>

      {section.entries?.map((entry) => (
        <ResumeEntry key={`${section.title}-${entry.title}`} entry={entry} />
      ))}

      {section.subsections?.map((subsection, subIndex) => (
        <div key={`${section.title}-${subsection.title}`}>
          <h3 className="resume-cv__subsection-heading">
            {index + 1}.{subIndex + 1} {subsection.title}
          </h3>
          <ul className="resume-cv__itemize">
            {subsection.items.map((item, itemIndex) => (
              <li key={itemIndex} dangerouslySetInnerHTML={{ __html: item }} />
            ))}
          </ul>
        </div>
      ))}

      {section.content ? (
        <p
          className="resume-cv__section-content"
          dangerouslySetInnerHTML={{ __html: section.content }}
        />
      ) : null}
    </section>
  );
}

function Resume() {
  return (
    <Container fluid className="resume-section">
      <Particle />
      <div className="resume-cv">
        <ResumeHeader header={resumeData.header} />
        <ResumeAbstract abstract={resumeData.abstract} />
        {resumeData.sections.map((section, index) => (
          <ResumeSection key={section.title} index={index} section={section} />
        ))}
      </div>
    </Container>
  );
}

export default Resume;
